import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  isCommissionsOverviewTabVisible,
  isLeadPartnersTabVisible,
  areAnnouncementsHidden,
  areOnboardingInvitesDisabled
} from 'helpers/viewing-rules';
import { getCompany, getFrontendInstance } from 'helpers/getCompanyVariables';
import companyNames from 'constants/companyNames';

const companyShort = companyNames?.short ?? 'Company';
const company = getCompany();

const isSunshine = company === 'sunshine';
const isOneill = company === 'oneill';

const env = getFrontendInstance();

function closeMuiSelect() {
  // Click the invisible MUI Modal backdrop to close any open Select/Menu
  document.querySelector('.MuiModal-root .MuiBackdrop-root')?.click();
}

const TOUR_CARRIER_TAB_SKIP = /^(Appointed Carriers|By State)$/;

function isTourGroupCarrierTab(button) {
  const text = button?.textContent?.trim() ?? '';
  if (TOUR_CARRIER_TAB_SKIP.test(text)) return false;
  if (text.includes('Action Needed')) return false;
  return true;
}

function gridHasCarrierCards(gridSelector) {
  return Boolean(
    document.querySelector(`${gridSelector} button`) ||
      document.querySelector(`${gridSelector} .cursor-pointer`)
  );
}

/** If Appointed Carriers is empty, switch to the first group tab that has cards. */
function ensureCarrierTabWithCards(tabButtonsSelector, gridSelector, onDone) {
  if (gridHasCarrierCards(gridSelector)) {
    onDone();
    return;
  }

  const groupTabs = Array.from(
    document.querySelectorAll(`${tabButtonsSelector} button`)
  ).filter(isTourGroupCarrierTab);

  let index = 0;
  const tryNextTab = () => {
    if (gridHasCarrierCards(gridSelector) || index >= groupTabs.length) {
      onDone();
      return;
    }
    groupTabs[index]?.click();
    index += 1;
    setTimeout(tryNextTab, 450);
  };

  tryNextTab();
}

function clickFirstCarrierInGrid(gridSelector) {
  // CarrierCards render as divs with cursor-pointer, not as <button> elements.
  // Querying for `button` first would now incorrectly hit the "Get Appointed"
  // button that lives inside the same grid container.
  const card = document.querySelector(`${gridSelector} .cursor-pointer`);
  card?.click();
}

// Cached by invisible step 22 after horizontal scroll; step 23 reads it at highlight time
let _webinarsTabEl = null;

const BY_STATE_MAP_COPY =
  'This interactive map shows carrier availability across the U.S. Light blue indicates State Based Exchange (SBE) states. Dark blue indicates Federal Facilitated Exchange (FFM). Grey states have no carriers available.';

// by-state map copy; the carrier types filter only renders when carriers span more than one type
function byStateMapDescription(_user, { carrierTypeCount } = {}) {
  if ((carrierTypeCount ?? 0) < 2) return BY_STATE_MAP_COPY;
  return `${BY_STATE_MAP_COPY} Use the <b>Carrier types</b> filter to show only the carrier types you pick.`;
}

function getVisibleResourcesTabsContainer() {
  return Array.from(
    document.querySelectorAll('[data-tour="resources-tabs"]')
  ).find(el => {
    if (!el.offsetParent) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
}

function resolveWebinarsTab(container = getVisibleResourcesTabsContainer()) {
  if (!container) return null;
  const byAttr = Array.from(
    container.querySelectorAll('[data-tour="resources-tab-webinars"]')
  ).find(el => el.getBoundingClientRect().width > 0);
  if (byAttr) return byAttr;
  return (
    Array.from(container.querySelectorAll('[role="tab"]')).find(t =>
      t.textContent.includes('Webinar')
    ) ?? null
  );
}

// All 18 sections. adminOnly/showFor sections are filtered per user at runtime.
export const TOUR_SECTIONS = [
  {
    id: 'checklist',
    label: 'Your Portal Tour',
    selector: '[data-tour="checklist-panel"]',
    side: 'left',
    align: 'center',
    title: 'Welcome to the Portal Tour!',
    description:
      'This checklist will guide you through each section of the portal. Complete all sections to finish your onboarding and unlock the full portal.',
    skipNavigation: true
  },
  {
    id: 'home',
    label: 'Home',
    route: '/home',
    oneillOnly: true,
    autoStartIntro: {
      title: 'Welcome to Home',
      description:
        "Your dashboard is ready. We'll walk you through your profile menu, notifications, and the key tools on this page — then continue your portal tour from here."
    },
    steps: [
      // 1 — Welcome intro (centered popover, no element)
      {
        selector: () => null,
        title: `Welcome to ${companyShort}!`,
        description:
          "We're glad you're here. This quick tour will walk you through every section of the portal so you can hit the ground running.<br><br>Follow along and we'll show you everything you need to know.",
        side: 'bottom',
        align: 'center',
        showButtons: ['next']
      },
      // 2 — Click the profile trigger (invisible auto-advance)
      {
        selector: '[data-tour="page-header-menu"]',
        title: '',
        description: '',
        side: 'bottom',
        align: 'end',
        hideButtons: true,
        onHighlighted: (el, _step, driver) => {
          el?.click();
          setTimeout(() => driver?.moveNext(), 200);
        }
      },
      // 2 — Spotlight the open profile dropdown
      {
        selector: '[data-tour="profile-dropdown"]',
        title: 'Your Profile Menu',
        description: `Click your avatar to access account options: <b>My Account</b> (profile & settings), <b>Change Password</b>, ${
          isSunshine ? '' : '<b>Change 2FA Phone Number</b> '
        }and <b>Logout</b>.`,
        side: 'bottom',
        align: 'end',
        showButtons: ['next'],
        closeOnNext: () => {
          document.querySelector('[data-tour="page-header-menu"]')?.click();
        }
      },
      // 3 — Click the bell button (invisible auto-advance)
      {
        selector: '[data-tour="notifications-bell"]',
        title: '',
        description: '',
        side: 'bottom',
        align: 'end',
        hideButtons: true,
        onHighlighted: (el, _step, driver) => {
          el?.querySelector('button')?.click();
          setTimeout(() => driver?.moveNext(), 200);
        }
      },
      // 4 — Spotlight the open notifications panel
      {
        selector: '[data-tour="notifications-panel"]',
        title: 'Notifications',
        description:
          'Click the bell to see your latest notifications — announcements, alerts, and updates from your agency. A red dot means you have unread messages.',
        side: 'bottom',
        align: 'end',
        closeOnNext: () => {
          document
            .querySelector('[data-tour="notifications-bell"] button')
            ?.click();
        },
        onPrevClick: (_el, _step, { driver }) => {
          const fire = el =>
            el?.dispatchEvent(
              new MouseEvent('click', { bubbles: true, cancelable: true })
            );
          // 1. Close notifications panel
          fire(
            document.querySelector('[data-tour="notifications-bell"] button')
          );
          // 2. Open profile menu after notifications closes
          setTimeout(() => {
            fire(document.querySelector('[data-tour="page-header-menu"]'));
            // 3. Go directly to spotlight step — menu is already open, skip invisible click step
            setTimeout(() => driver.moveTo(2), 400);
          }, 400);
        }
      },
      // 5 — Carrier appointment badges
      {
        selector: '[data-tour="appt-badges"]',
        title: 'Carrier Appointment Cards',
        description: `These cards show your carrier appointment status: <b>${
          isSunshine ? 'Active' : 'Confirmed'
        }</b>${isSunshine ? ' and ' : ', '}<b>Pending</b> ${
          isSunshine ? '' : ' and <b>Need Action</b>'
        }. Click any card to go directly to your Carrier Appointments screen.`,
        side: 'bottom',
        onPrevClick: (_el, _step, { driver }) => {
          const fire = el =>
            el?.dispatchEvent(
              new MouseEvent('click', { bubbles: true, cancelable: true })
            );
          // Open notifications panel, then spotlight it — skip invisible click step (index 3)
          fire(
            document.querySelector('[data-tour="notifications-bell"] button')
          );
          setTimeout(() => driver.moveTo(4), 400);
        }
      },
      // 6 — Commission Insights section overview (O'Neill only)
      {
        selector: '[data-tour="profile-charts"]',
        title: 'Commission Insights',
        // MEMBER_COUNT_HIDDEN — temporarily hidden: the provider's member count is miscalculated. restore once fixed on the provider's side.
        // original: 'This section gives you a quick snapshot of your commission activity — including your member count, recent earnings trend, and how your commissions are distributed across carriers.'
        description:
          'This section gives you a quick snapshot of your commission activity — including your recent earnings trend and how your commissions are distributed across carriers.',
        side: 'bottom',
        align: 'start',
        stepShowFor: () => isOneill
      },
      // 7 — Member Count card (removed from tour)
      // MEMBER_COUNT_HIDDEN
      {
        selector: '[data-tour="profile-charts-member-count"]',
        title: 'Member Count',
        description:
          'Your current total member count across all carriers and policies.',
        side: 'right',
        align: 'center',
        stepShowFor: () => false
      },
      // 8 — Commission trend chart (removed from tour)
      {
        selector: '[data-tour="profile-charts-trend"]',
        title: 'Commission Trend',
        description:
          'This chart shows your monthly commission totals over the last 6 months — a quick way to see how your earnings are trending over time.',
        side: 'top',
        align: 'start',
        stepShowFor: () => false
      },
      // 9 — Carrier mix (removed from tour)
      {
        selector: '[data-tour="profile-charts-carrier-mix"]',
        title: 'Carrier Mix',
        description:
          'A breakdown of your commissions by carrier. Each row shows the carrier name, commission amount, and its percentage share of your total earnings.',
        side: 'top',
        align: 'start',
        stepShowFor: () => false
      },
      // 10 — View all commissions button (removed from tour)
      {
        selector: '[data-tour="profile-charts-view-all"]',
        title: 'View All Commissions',
        description:
          'Click <b>View all commissions</b> to go to the full Commissions Overview screen, where you can filter, analyze, and drill down into all of your commission data.',
        side: 'bottom',
        align: 'end',
        stepShowFor: () => false
      },
      // 11 — Calendar block
      {
        selector: '[data-tour="calendar"]',
        title: 'Calendar',
        description:
          'Your calendar shows important dates, company events, and your personal scheduled events. Click any day to filter the event list by that date.',
        side: 'right'
      },
      // 12 — Open the event-filter MUI Select (invisible auto-advance step)
      {
        selector: '[data-tour="calendar-filter"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          const trigger = document.querySelector(
            '[data-tour="calendar-filter"] .MuiSelect-select'
          );
          // MUI Select opens on mousedown, not click
          trigger?.dispatchEvent(
            new MouseEvent('mousedown', { bubbles: true, button: 0 })
          );
          // 350ms — enough for the MUI Portal to render the Paper
          setTimeout(() => driver?.moveNext(), 350);
        }
      },
      // 13 — Spotlight the open MUI menu list
      {
        selector: '[data-tour="calendar-filter-menu"]',
        title: 'Event Filters',
        description: `Use this filter to switch between <b>All Events</b>, <b>My Events</b> (your personal calendar), and <b>${companyShort} Events</b> (company-wide dates).`,
        side: 'right',
        align: 'start',
        closeOnNext: closeMuiSelect,
        onPrevClick: (_el, _step, { driver }) => {
          closeMuiSelect();
          // Calendar is 2 steps back (invisible filter-opener sits between them)
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 350);
        }
      },
      // 14 — Announcements
      {
        selector: '[data-tour="announcements"]',
        title: 'Announcements',
        description:
          'Stay up to date with company announcements here. They rotate automatically — use the arrows to browse through all of them.',
        side: 'left',
        // the carousel is not rendered when the agency hides announcements, so the
        // step has to drop out or it would spotlight a node that does not exist
        stepShowFor: user => !areAnnouncementsHidden(user?.agencyData),
        onPrevClick: (_el, _step, { driver }) => {
          // Reopen the calendar filter dropdown, then return to the filter-menu step
          const trigger = document.querySelector(
            '[data-tour="calendar-filter"] .MuiSelect-select'
          );
          trigger?.dispatchEvent(
            new MouseEvent('mousedown', { bubbles: true, button: 0 })
          );
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 1), 400);
        }
      },
      // 15 — Agent Owner Card: Smooth Sailing Starts Here (O'Neill only)
      {
        selector: '[data-tour="agent-owner-card"]',
        title: 'Smooth Sailing Starts Here',
        description:
          "This card displays the best way for you to get in contact with the O'Neill Marketing team — click <b>Let's Connect</b> to schedule a call directly through their booking page.",
        side: 'left',
        align: 'start',
        stepShowFor: () => isOneill,
        onPrevClick: (_el, _step, { driver }) => {
          const idx = driver.getActiveIndex();
          // the carousel node is in the DOM whenever the announcements step is part
          // of the tour (it keeps its data-tour even with no announcements to show),
          // so the previous step needs no extra setup
          if (document.querySelector('[data-tour="announcements"]')) {
            driver.moveTo(idx - 1);
            return;
          }
          // announcements are hidden by the agency, so the step was filtered out and
          // this card follows the calendar filter menu directly — reopen that dropdown
          const trigger = document.querySelector(
            '[data-tour="calendar-filter"] .MuiSelect-select'
          );
          trigger?.dispatchEvent(
            new MouseEvent('mousedown', { bubbles: true, button: 0 })
          );
          setTimeout(() => driver.moveTo(idx - 1), 400);
        }
      },
      // 16 — Zoho SalesIQ chat widget (last step, O'Neill only)
      {
        selector: '#zsiq_float',
        title: 'Chat Support',
        description:
          'Need help? Click this button any time to start a chat — available directly from every page in the portal.',
        side: 'top',
        align: 'end',
        scrollIntoViewOptions: false,
        stepShowFor: () => isOneill && env !== 'production'
      }
    ]
  },
  {
    id: 'agency-management',
    label: 'Agency Dashboard',
    getRoute: user => `/agency/dashboard/${user?.Account_Name?.id}`,
    adminOnly: true,
    matchesPage: () => {
      const active = document.querySelector(
        '[data-tour="agency-section-tabs"] [role="tab"][aria-selected="true"]'
      );
      if (!active) return true;
      return !active.textContent.includes('Onboarding');
    },
    steps: [
      // 1 — Agents table overview (spotlight thead only)
      {
        selector: '[data-tour="agency-table-head"]',
        title: 'Agents Table',
        description:
          'This table lists all agents in your agency — name, email, NPN, position, status, the agency they belong to, and warning flags (e.g. agents who have gone 9+ months without a policy sold).',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 2 — View As Agent column
      {
        selector: '[data-tour="agency-view-col"]',
        title: 'View As Agent',
        description:
          "The <b>View</b> button opens the portal from that agent's perspective in a new tab — useful to verify what an agent sees. Your own row is always disabled.",
        side: 'bottom',
        align: 'start'
      },
      // 3 — Offboarding column
      {
        selector: '[data-tour="agency-offboard-col"]',
        title: 'Offboarding',
        description:
          'Admins can initiate the offboarding process for any agent. An agent cannot offboard themselves — their own row is disabled. If an agent is ineligible, a reason is shown beneath the button.',
        side: 'bottom',
        align: 'end'
      },
      // 4 — Filters toolbar overview
      {
        selector: '[data-tour="agency-filters"]',
        title: 'Agent Filters',
        description:
          'Search agents by name, email, NPN, or agency. Use <b>Position</b> to filter by role and <b>Sort</b> to control the display order.',
        side: 'top',
        align: 'start'
      },
      // 5 — Open Position filter (invisible auto-advance)
      {
        selector: '[data-tour="agency-position-filter"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          const trigger = document.querySelector(
            '[data-tour="agency-position-filter"] .MuiSelect-select'
          );
          trigger?.dispatchEvent(
            new MouseEvent('mousedown', { bubbles: true, button: 0 })
          );
          setTimeout(() => driver?.moveNext(), 350);
        }
      },
      // 6 — Position filter menu open
      {
        selector: '[data-tour="agency-position-menu"]',
        title: 'Position Filter',
        description:
          'Filter agents by their role in the agency: <b>All</b>, or a specific position like Agent, Manager, etc. The options update based on the agents currently loaded.',
        side: 'right',
        align: 'start',
        closeOnNext: closeMuiSelect,
        onPrevClick: (_el, _step, { driver }) => {
          closeMuiSelect();
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 350);
        }
      },
      // 7 — Open Sort filter (invisible auto-advance)
      {
        selector: '[data-tour="agency-sort-filter"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          const trigger = document.querySelector(
            '[data-tour="agency-sort-filter"] .MuiSelect-select'
          );
          trigger?.dispatchEvent(
            new MouseEvent('mousedown', { bubbles: true, button: 0 })
          );
          setTimeout(() => driver?.moveNext(), 350);
        }
      },
      // 8 — Sort filter menu open
      {
        selector: '[data-tour="agency-sort-menu"]',
        title: 'Sort Order',
        description:
          "Sort agents by <b>Name A–Z</b>, <b>Name Z–A</b>, or <b>Agency A–Z</b> to quickly find who you're looking for.",
        side: 'right',
        align: 'start',
        closeOnNext: closeMuiSelect,
        onPrevClick: (_el, _step, { driver }) => {
          closeMuiSelect();
          const trigger = document.querySelector(
            '[data-tour="agency-position-filter"] .MuiSelect-select'
          );
          trigger?.dispatchEvent(
            new MouseEvent('mousedown', { bubbles: true, button: 0 })
          );
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400);
        }
      },
      // 9 — Agent Rank Tabs
      {
        selector: '[data-tour="agency-rank-tabs"]',
        title: 'Agent Status Tabs',
        description:
          'Filter agents by status: <b>Active</b> (currently contracted), <b>Holds</b> (compliance or contracting hold), <b>Offboarding</b> (in process), <b>Inactive</b>, or <b>All Agents</b> to see everyone at once.',
        side: 'bottom',
        align: 'start',
        onPrevClick: (_el, _step, { driver }) => {
          const trigger = document.querySelector(
            '[data-tour="agency-sort-filter"] .MuiSelect-select'
          );
          trigger?.dispatchEvent(
            new MouseEvent('mousedown', { bubbles: true, button: 0 })
          );
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 1), 400);
        }
      },
      // 10 — Agency selector
      {
        selector: '[data-tour="agency-selector"]',
        title: 'Agency Selector',
        description:
          'Select one or more agencies from your downline to load their agents into the table. You can combine multiple agencies to get a unified view of all their agents.',
        side: 'bottom',
        align: 'start'
      },
      // 12 — Update Table button (last step)
      {
        selector: '[data-tour="agency-update-table"]',
        title: 'Update Table',
        description:
          'After selecting the desired agencies, click <b>Update Table</b> to reload the agents list with the selected agencies applied.',
        side: 'bottom',
        align: 'start'
      }
    ]
  },
  {
    id: 'onboarding-dashboard',
    label: 'Onboarding Dashboard',
    getRoute: user => `/agency/dashboard/${user?.Account_Name?.id}`,
    adminOnly: true,
    matchesPage: () => {
      const active = document.querySelector(
        '[data-tour="agency-section-tabs"] [role="tab"][aria-selected="true"]'
      );
      return !!active?.textContent.includes('Onboarding');
    },
    steps: [
      // 1 — Invisible: click the "Onboarding Dashboard" section tab to switch view
      {
        selector: '[data-tour="agency-section-tabs"]',
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          const tabs = document.querySelectorAll(
            '[data-tour="agency-section-tabs"] [role="tab"]'
          );
          const onboardingTab = Array.from(tabs).find(t =>
            t.textContent.includes('Onboarding')
          );
          onboardingTab?.click();
          setTimeout(() => driver?.moveNext(), 600);
        }
      },
      // 2 — Search & filters bar
      {
        selector: '[data-tour="onboarding-filters"]',
        title: 'Search & Filters',
        description:
          'Search agents by name or NPN. Use the <b>Status</b> filter to narrow records by pipeline stage. When multiple agencies are selected, the <b>Related agency</b> filter lets you focus on one agency at a time.',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 4 — Status filter
      {
        selector: '[data-tour="onboarding-status-filter"]',
        title: 'Status Filter',
        description:
          "Filter onboarding records by pipeline stage: <b>Onboarding Sent to Agent</b>, <b>Onboarding Submitted to O'Neill</b>, <b>Producer Agreement Signed</b>, or <b>Onboarding Approved</b>. Use <b>Select All</b> to see every record.",
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 5 — Onboarding table (spotlight thead to avoid tall-element sizing issue)
      {
        selector: '[data-tour="onboarding-table"]',
        title: 'Onboarding Records',
        description:
          'Each row is an onboarding record with <b>Agent</b>, <b>NPN</b>, <b>Agency</b>, <b>Progress</b>, <b>Status</b>, <b>Flag</b> and <b>Started</b>.',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 6 — Spotlight the Actions column header
      {
        selector: '[data-tour="onboarding-actions-header"]',
        title: 'Cancel Onboarding',
        description:
          'When an onboarding record is still active, a <b style="color:#dc2626">Cancel onboarding</b> button appears in the <b>Actions</b> column. This lets admins remove the agent from the current flow if needed.',
        side: 'bottom',
        align: 'end',
        scrollIntoViewOptions: false
      },
      // 7 — Invisible: click the Cancel button to open the confirmation modal
      {
        selector: '[data-tour="onboarding-cancel-btn"]',
        title: '',
        description: '',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          document
            .querySelector('[data-tour="onboarding-cancel-btn"]')
            ?.click();
          setTimeout(() => driver?.moveNext(), 600);
        }
      },
      // 8 — Cancel confirmation modal — content
      {
        selector: '[data-tour="onboarding-cancel-modal"]',
        title: 'Confirmation Panel',
        description:
          "Clicking <b>Cancel onboarding</b> opens this confirmation showing the agent's <b>name</b>, <b>NPN</b>, and current <b>status</b>. Review the details carefully — this action cannot be undone.",
        side: 'bottom',
        align: 'center',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('[data-tour="onboarding-cancel-modal-close"]')
            ?.click();
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400);
        }
      },
      // 9 — Cancel confirmation modal — footer actions (last modal step)
      {
        selector: '[data-tour="onboarding-cancel-modal-footer"]',
        title: 'Confirm or Go Back',
        description:
          'Use <b>Go back</b> to dismiss without making changes, or <b>Cancel onboarding</b> to permanently remove the agent from the pipeline.',
        side: 'top',
        align: 'end',
        scrollIntoViewOptions: false,
        closeOnNext: () => {
          document
            .querySelector('[data-tour="onboarding-cancel-modal-close"]')
            ?.click();
        }
      }
    ]
  },
  {
    id: 'agency-settings',
    label: 'Settings & Invites',
    getRoute: user => `/agency/settings/${user?.Account_Name?.id}`,
    adminOnly: true,
    steps: [
      // 1 — Invite agents section (hidden if agency has invites disabled)
      {
        selector: '[data-tour="settings-invite"]',
        title: 'Invite Agents',
        description: `Send the agent onboarding form via email. Use <b>Email Myself</b> to get a secure link for your own use, or <b>Email Agent</b> to send the form directly to a new agent's inbox.${
          isOneill
            ? ' To onboard many agents at once, use <b>Bulk upload</b> to send invites from a CSV file.'
            : ''
        }`,
        side: 'right',
        align: 'start',
        stepShowFor: user => !areOnboardingInvitesDisabled(user?.agencyData)
      },
      // 2 — Form preferences card
      {
        selector: '[data-tour="settings-onboarding"]',
        title: 'Agency Settings',
        description:
          "Use these checkboxes to hide specific carrier categories from your downline agents' onboarding forms. Enter your <b>HealthSherpa Agency Code</b> and click <b>Save preferences</b> to apply changes.",
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 3a — invisible: scroll to banking card before spotlighting it
      {
        selector: '[data-tour="settings-invite"]',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="settings-banking"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top:
                    el.scrollTop +
                    target.getBoundingClientRect().top -
                    el.getBoundingClientRect().top -
                    16,
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          setTimeout(() => driver.moveNext(), 450);
        }
      },
      // 3b — Banking information (last step)
      {
        selector: '[data-tour="settings-banking"]',
        title: 'Agency Banking Information',
        description:
          "Your agency's banking details are shown here. Click <b>Edit</b> to update your bank name, routing number, or account number.",
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false
      }
    ]
  },
  {
    id: 'appointment-center',
    label: 'Appointment Center',
    getRoute: user => `/agency/appointments/${user?.Account_Name?.id}`,
    adminOnly: true,
    steps: [
      // 1 — Export buttons
      {
        selector: '[data-tour="appt-center-export"]',
        title: 'Export Appointments',
        description:
          "Download a report of your agency's <b>Confirmed</b> or <b>Pending</b> appointments as a spreadsheet — useful for tracking carrier contracting progress.",
        side: 'bottom',
        align: 'end',
        scrollIntoViewOptions: false
      },
      // 2 — Quick filter tabs
      {
        selector: '[data-tour="appt-center-quick-filters"]',
        title: 'Quick Filters',
        description:
          '<b>Needs attention</b> highlights appointments that require action from you or the agent. <b>Stalled</b> flags appointments that have been pending agent action for 14 days or more. <b>All open</b> shows every pending appointment.',
        side: 'bottom',
        align: 'start'
      },
      // 3 — Filter dropdowns
      {
        selector: '[data-tour="appt-center-filters"]',
        title: 'Filters',
        description:
          'Narrow down the list by <b>Agent</b>, <b>Carrier</b>, or <b>Status</b>. You can combine multiple filters to quickly zero in on specific appointments.',
        side: 'bottom',
        align: 'start'
      },
      // 4 — Agent cards
      {
        selector: '[data-tour="appt-center-cards"]',
        title: 'Pending Appointments',
        description:
          "Each card shows an agent's open appointment requests — the carrier, appointment type, blocking action, and how long it's been stalled.",
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 5 — View Agent button (last step)
      {
        selector: '[data-tour="appt-center-view-agent"]',
        title: 'View Agent',
        description:
          'Click <b>View Agent</b> to open the portal from that agent\'s perspective — you\'ll see their carrier appointments, profile, and data exactly as they would. This is the same "View As Agent" feature available in the Agency Dashboard.',
        side: 'bottom',
        align: 'end',
        scrollIntoViewOptions: false
      }
    ]
  },
  {
    id: 'carrier-appointments',
    label: 'Carrier Appointments',
    route: '/carrier-appointments',
    showFor: (_user, { isAgentLimited }) => !isAgentLimited,
    // Runs before Driver.js starts — goes back from carrier detail if open, then switches to Health tab
    setup: () => {
      const backBtn = document.querySelector(
        '[data-tour="carrier-detail-back"]'
      );
      if (backBtn) {
        backBtn.click();
        // Wait for the main grid to re-render, then switch to Health tab
        setTimeout(() => {
          const tabs = document.querySelectorAll(
            '[data-tour="carrier-appt-tab-buttons"] button'
          );
          Array.from(tabs)
            .find(t => t.textContent.trim() === 'Health')
            ?.click();
        }, 400);
        return 600; // extra ms added to the 500ms base delay so Driver.js waits for the grid
      }
      const tabs = document.querySelectorAll(
        '[data-tour="carrier-appt-tab-buttons"] button'
      );
      Array.from(tabs)
        .find(t => t.textContent.trim() === 'Health')
        ?.click();
    },
    steps: [
      // 0 — Carrier Tabs
      {
        selector: '[data-tour="carrier-appt-tab-buttons"]',
        title: 'Carrier Tabs',
        description: (_user, { parentAppointmentGroups }) => {
          const groups = [...(parentAppointmentGroups || [])];
          const groupTabs = groups.length
            ? `Tabs like ${groups
                .map(g => `<b>${g}</b>`)
                .join(' and ')} let you browse available carriers by type. `
            : '';
          return `<b>Appointed Carriers</b> shows all your current and requested appointments. ${groupTabs}<b>By State</b> shows an interactive USA map of carrier availability.`;
        },
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 1 — Status legend
      {
        selector: '[data-tour="carrier-appt-legend"]',
        title: 'Appointment Status Colors',
        description:
          'Color indicators next to each carrier icon show your current appointment status:',
        side: 'left',
        align: 'start',
        scrollIntoViewOptions: false,
        onPopoverRender: popoverEl => {
          const descEl = popoverEl.description;
          const container = document.createElement('div');
          descEl.appendChild(container);
          const items = [
            ...(isOneill
              ? [
                  {
                    color: '#f22344',
                    label: 'Pending Agent Action',
                    note: 'Check your email or the carrier portal for next steps.'
                  }
                ]
              : []),
            {
              color: '#f4a100',
              label: 'Pending',
              note: 'Your application is in review, no action needed on your end right now.'
            },
            {
              color: '#00a0eb',
              label: isSunshine ? 'Active' : 'Confirmed',
              note: 'Fully appointed.'
            }
          ];
          createRoot(container).render(
            React.createElement(
              'div',
              {
                style: {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  marginTop: 8
                }
              },
              items.map(({ color, label, note }) =>
                React.createElement(
                  'div',
                  {
                    key: label,
                    style: { display: 'flex', alignItems: 'center', gap: 8 }
                  },
                  React.createElement('span', {
                    style: {
                      display: 'inline-block',
                      minWidth: 11,
                      height: 11,
                      borderRadius: '50%',
                      background: color,
                      flexShrink: 0
                    }
                  }),
                  React.createElement(
                    'span',
                    { style: { fontSize: 13 } },
                    React.createElement('b', null, label),
                    ': ',
                    note
                  )
                )
              )
            )
          );
        }
      },
      // 2b — Invisible: leave Appointed Carriers if empty; open first group tab with cards
      {
        selector: '[data-tour="carrier-appt-grid"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          ensureCarrierTabWithCards(
            '[data-tour="carrier-appt-tab-buttons"]',
            '[data-tour="carrier-appt-grid"]',
            () => setTimeout(() => driver?.moveNext(), 300)
          );
        }
      },
      // 3 — Main carrier cards grid
      {
        selector: '[data-tour="carrier-appt-grid"]',
        title: 'Carrier Cards',
        description:
          "Each icon represents a carrier. Click any one to open a detail panel — you'll see your appointment status, a state-by-state breakdown (for carriers that track states), and options to request new appointments, state additions or a transfer.",
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 4 — Invisible: click the first carrier card to open the sidebar
      {
        selector: '[data-tour="carrier-appt-grid"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          clickFirstCarrierInGrid('[data-tour="carrier-appt-grid"]');
          setTimeout(() => driver?.moveNext(), 600);
        }
      },
      // 5 — Sidebar: appointment request panel
      {
        selector: '[data-tour="carrier-appt-sidebar"]',
        title: 'Appointment Request Panel',
        description: `This panel shows your <b>appointment status</b> with the selected carrier, followed by any notes that carrier shares. Select your states, choose a transfer type if applicable, and click Send request to submit to ${companyShort} — a few carriers instead ask you to fill out a short contracting form here. Only request appointments with carriers you plan to actively sell. Carriers typically terminate appointments after <b>12 months</b> of no production, and reappointment isn't always immediate.`,
        side: 'left',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          document.querySelector('[data-tour="carrier-detail-back"]')?.click();
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400);
        }
      },
      // 6 — Invisible: go back and switch to By State tab
      {
        selector: '[data-tour="carrier-detail-back"]',
        title: '',
        description: '',
        side: 'right',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          document.querySelector('[data-tour="carrier-detail-back"]')?.click();
          setTimeout(() => {
            document
              .querySelector('[data-tour="carrier-appt-by-state-tab"]')
              ?.click();
            setTimeout(() => driver?.moveNext(), 500);
          }, 350);
        }
      },
      // 7 — By State map overview
      {
        selector: '[data-tour="carrier-appt-map"]',
        title: 'Appointments By State',
        description: byStateMapDescription,
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          // Switch to a tab with carrier cards, click the first card, then go to Request Panel
          ensureCarrierTabWithCards(
            '[data-tour="carrier-appt-tab-buttons"]',
            '[data-tour="carrier-appt-grid"]',
            () => {
              clickFirstCarrierInGrid('[data-tour="carrier-appt-grid"]');
              const idx = driver.getActiveIndex();
              setTimeout(() => driver.moveTo(idx - 2), 600);
            }
          );
        }
      },
      // 8 — Invisible: click a state to open the carrier sidebar
      {
        selector: '[data-tour="carrier-appt-map"]',
        title: '',
        description: '',
        side: 'right',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          // SVG path elements require dispatchEvent instead of .click()
          const stateEl =
            document.querySelector(
              '[data-tour="carrier-appt-map"] [data-name="FL"]'
            ) ||
            document.querySelector('[data-tour="carrier-appt-map"] .usa-state');
          if (stateEl) {
            stateEl.dispatchEvent(
              new MouseEvent('click', { bubbles: true, cancelable: true })
            );
          }
          setTimeout(() => driver?.moveNext(), 600);
        }
      },
      // 9 — By State carrier sidebar (last step)
      {
        selector: '[data-tour="carrier-appt-by-state-sidebar"]',
        title: 'Carriers Available in This State',
        description:
          "When you click a state, this panel lists every O'Neill carrier available there — grouped by your appointment status: <b>Confirmed</b>, <b>Pending</b>, <b>Needs Action</b>, and <b>Not Requested</b>. The small label under each carrier shows its category. Click any carrier to open the full appointment request panel.",
        side: 'left',
        align: 'start',
        scrollIntoViewOptions: false
      }
    ]
  },
  {
    id: 'submissions',
    label: 'Submissions',
    route: '/customers/policies',
    showFor: (user, { isAgentLimited }) =>
      !isAgentLimited && user?.Customers_Tab === 'Show',
    steps: [
      // 1 — Toolbar: search + show filters button
      {
        selector: '[data-tour="submissions-toolbar"]',
        title: 'Search Submissions',
        description:
          'Search by client name, policy number, phone, or email. The number on the <b>Show Filters</b> button shows how many active filters are currently applied.',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 2 (invisible) — Click "Show Filters" to open the sidebar, then reset scroll
      {
        selector: '[data-tour="submissions-show-filters"]',
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          document
            .querySelector('[data-tour="submissions-show-filters"]')
            ?.click();
          setTimeout(() => {
            let el = document.querySelector(
              '[data-tour="submissions-toolbar"]'
            );
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                el.scrollTo({ top: 0 });
                break;
              }
              el = el.parentElement;
            }
            driver?.moveNext();
          }, 500);
        }
      },
      // 3 — Filters header (small sticky element, avoids full-panel height issue)
      {
        selector: '[data-tour="submissions-filters-header"]',
        title: 'Submission Filters',
        description:
          'Narrow down submissions by carrier, state, carrier appointment status, AOR status, and more. You can save your filter combinations and reload them any time from the top of this panel.',
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false,
        closeOnNext: () => {
          document
            .querySelector('[data-tour="submissions-show-filters"]')
            ?.click();
        },
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('[data-tour="submissions-show-filters"]')
            ?.click();
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400);
        }
      },
      // 4 — Table overview
      {
        selector: '[data-tour="submissions-table"]',
        title: 'Your Submissions',
        description:
          'Each row is a client policy. You can see carrier, state, NPN, appointment status, and effective date. Click the arrow on any row to expand it and see more details.',
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('[data-tour="submissions-show-filters"]')
            ?.click();
          setTimeout(() => {
            let el = document.querySelector(
              '[data-tour="submissions-toolbar"]'
            );
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                el.scrollTo({ top: 0 });
                break;
              }
              el = el.parentElement;
            }
            const idx = driver.getActiveIndex();
            driver.moveTo(idx - 1);
          }, 500);
        }
      },
      // 5 (invisible) — Expand the first row
      {
        selector: '[data-tour="submissions-table"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const expandBtn = document.querySelector(
            '#customers-table-body tbody tr button'
          );
          expandBtn?.click();
          setTimeout(() => driver?.moveNext(), 500);
        }
      },
      // 6 — Expanded row detail panel
      {
        selector: '[data-tour="submissions-row-detail"]',
        title: 'Policy Details',
        description:
          'The expanded panel shows additional fields: plan name, gross and net premium, residence, writing agent, agency, email, phone, zip code, and submission date.',
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false,
        closeOnNext: () => {
          document
            .querySelector('#customers-table-body tbody tr button')
            ?.click();
        },
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('#customers-table-body tbody tr button')
            ?.click();
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400);
        }
      },
      // 7 — Carrier Appt Status column header (last step)
      {
        selector: '[data-tour="submissions-carrier-status"]',
        title: 'Carrier Appointment Status',
        description:
          'This column shows whether the writing agent is appointed with the carrier for this policy: <b>Appointed</b>, <b>Not Appointed</b>, <b>In Progress</b>, or <b>N/A</b>. When an agent is not appointed, a <b>Take Action</b> button appears — clicking it opens a modal where you can request the appointment or choose to ignore the carrier for that policy.',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('#customers-table-body tbody tr button')
            ?.click();
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 1), 500);
        }
      }
    ]
  },
  {
    id: 'commissions-overview',
    // matches the sidebar tab for this route, which sunshine renames
    label: isSunshine ? 'Book of Business Insights' : 'Commissions Overview',
    route: '/commissions/overview',
    showFor: user => isCommissionsOverviewTabVisible(user, true),
    steps: [
      // 1 — Toolbar: Show Filters + date type selector
      {
        selector: '[data-tour="commissions-toolbar"]',
        title: 'Date Filter & Controls',
        description:
          'Use the <b>Show Filters</b> button to open the full filter panel. The selector on the right lets you group all data by <b>Coverage Month</b>, <b>Statement Date</b>, or <b>Statement Month</b> — every chart and table updates instantly.',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 2 (invisible) — Open filters sidebar, then reset scroll before advancing
      {
        selector: '[data-tour="commissions-show-filters"]',
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          document
            .querySelector('[data-tour="commissions-show-filters"]')
            ?.click();
          setTimeout(() => {
            // After the layout reflows, scroll the overflow container back to top
            let el = document.querySelector(
              '[data-tour="commissions-toolbar"]'
            );
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                el.scrollTo({ top: 0 });
                break;
              }
              el = el.parentElement;
            }
            driver?.moveNext();
          }, 500);
        }
      },
      // 3 — Filters header (small sticky element, avoids full-panel height issue)
      {
        selector: '[data-tour="commissions-filters-header"]',
        title: 'Commission Filters',
        description:
          'Filter by agent, agency, carrier, state, date range, and agent status. Click <b>Apply Filters</b> to refresh all charts and tables with your selection.',
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false,
        closeOnNext: () => {
          document
            .querySelector('[data-tour="commissions-show-filters"]')
            ?.click();
        },
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('[data-tour="commissions-show-filters"]')
            ?.click();
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400);
        }
      },
      // 4a (invisible) — scroll to KPI cards
      {
        selector: '[data-tour="commissions-toolbar"]',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="commissions-kpi"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top:
                    el.scrollTop +
                    target.getBoundingClientRect().top -
                    el.getBoundingClientRect().top -
                    16,
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          setTimeout(() => driver.moveNext(), 450);
        }
      },
      // 4b — KPI Cards
      {
        selector: '[data-tour="commissions-kpi"]',
        title: 'Commission Summary',
        // MEMBER_COUNT_HIDDEN — temporarily hidden: the provider's member count is miscalculated. restore once fixed on the provider's side.
        // original: 'Your <b>Total Commissions</b> for the filtered period. Use the <b>All / Regular / Bonus</b> tabs to switch between commission types. The cards on the right show <b>YTD</b> earnings, <b>Current Member Count</b>, and your <b>Top Carrier</b> by volume.'
        description:
          'Your <b>Total Commissions</b> for the filtered period. Use the <b>All / Regular / Bonus</b> tabs to switch between commission types. The cards on the right show <b>YTD</b> earnings and your <b>Top Carrier</b> by volume.',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('[data-tour="commissions-show-filters"]')
            ?.click();
          setTimeout(() => {
            let el = document.querySelector(
              '[data-tour="commissions-toolbar"]'
            );
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                el.scrollTo({ top: 0 });
                break;
              }
              el = el.parentElement;
            }
            const idx = driver.getActiveIndex();
            driver.moveTo(idx - 2);
          }, 500);
        }
      },
      // 5a (invisible) — scroll to bar chart
      {
        selector: '[data-tour="commissions-kpi"]',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="commissions-graph"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top:
                    el.scrollTop +
                    target.getBoundingClientRect().top -
                    el.getBoundingClientRect().top -
                    16,
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          setTimeout(() => driver.moveNext(), 450);
        }
      },
      // 5b — Bar chart
      {
        selector: '[data-tour="commissions-graph"]',
        title: 'Commissions Over Time',
        description:
          'This bar chart shows your commission earnings broken down by carrier for each period. Each color represents a different carrier. Hover over any bar to see a detailed breakdown by carrier.',
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 6a (invisible) — scroll to pie chart
      {
        selector: '[data-tour="commissions-graph"]',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="commissions-pie"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top:
                    el.scrollTop +
                    target.getBoundingClientRect().top -
                    el.getBoundingClientRect().top -
                    16,
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          setTimeout(() => driver.moveNext(), 450);
        }
      },
      // 6b — Pie chart
      {
        selector: '[data-tour="commissions-pie"]',
        title: 'Carrier Breakdown',
        description:
          'The pie chart shows how your total commissions are distributed across your top carriers. Hover over any slice to see the exact commission amount and percentage for that carrier.',
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          const target = document.querySelector(
            '[data-tour="commissions-graph"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top: Math.max(
                    0,
                    el.scrollTop +
                      target.getBoundingClientRect().top -
                      el.getBoundingClientRect().top -
                      16
                  ),
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 450);
        }
      },
      // 7a (invisible) — scroll to map
      {
        selector: '[data-tour="commissions-pie"]',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="commissions-map"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top:
                    el.scrollTop +
                    target.getBoundingClientRect().top -
                    el.getBoundingClientRect().top -
                    16,
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          setTimeout(() => driver.moveNext(), 450);
        }
      },
      // 7b — USA Map
      {
        selector: '[data-tour="commissions-map"]',
        title: 'Commissions by State',
        description:
          'Each state is color-coded by commission volume — darker blue means higher earnings in that region. Hover over any state to see the exact commission amount.',
        side: 'left',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          const target = document.querySelector(
            '[data-tour="commissions-pie"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top: Math.max(
                    0,
                    el.scrollTop +
                      target.getBoundingClientRect().top -
                      el.getBoundingClientRect().top -
                      16
                  ),
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 450);
        }
      },
      // 8a — invisible: scroll to commissions table before spotlighting it
      {
        selector: '[data-tour="commissions-map"]',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="commissions-table"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top: Math.max(
                    0,
                    el.scrollTop +
                      target.getBoundingClientRect().top -
                      el.getBoundingClientRect().top -
                      el.clientHeight * 0.15
                  ),
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          setTimeout(() => driver.moveNext(), 450);
        }
      },
      // 8b — Commissions Table
      {
        selector: '[data-tour="commissions-table"]',
        title: 'Commissions Table',
        description:
          'A detailed breakdown by carrier and period. Each row is a carrier, each column is a time period, and the <b>Total</b> column sums across all periods. <span style="color:#16a34a">Green</span> values are positive, <span style="color:#dc2626">red</span> are negative.',
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          const target = document.querySelector(
            '[data-tour="commissions-map"]'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top: Math.max(
                    0,
                    el.scrollTop +
                      target.getBoundingClientRect().top -
                      el.getBoundingClientRect().top -
                      16
                  ),
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 450);
        }
      },
      // 9a — invisible: scroll to member count table before spotlighting it
      // MEMBER_COUNT_HIDDEN — temporarily hidden: the provider's member count is miscalculated. restore once fixed on the provider's side.
      {
        selector: '[data-tour="commissions-table"]',
        hideButtons: true,
        scrollIntoViewOptions: false,
        stepShowFor: () => false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="commissions-members"]'
          );
          const main = document.querySelector('main');
          if (target && main) {
            const offset =
              main.scrollTop +
              target.getBoundingClientRect().top -
              main.getBoundingClientRect().top -
              main.clientHeight * 0.15;
            main.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
          }
          setTimeout(() => driver.moveNext(), 450);
        }
      },
      // 9b — Member Count Table (last step)
      // MEMBER_COUNT_HIDDEN
      {
        selector: '[data-tour="commissions-members"]',
        title: 'Member Count Table',
        description:
          'Shows the number of members per carrier for each period, organized by the selected date type. The total member count across all carriers is displayed above the table.',
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false,
        stepShowFor: () => false,
        onPrevClick: (_el, _step, { driver }) => {
          const target = document.querySelector(
            '[data-tour="commissions-table"]'
          );
          const main = document.querySelector('main');
          if (target && main) {
            const offset =
              main.scrollTop +
              target.getBoundingClientRect().top -
              main.getBoundingClientRect().top -
              main.clientHeight * 0.15;
            main.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
          }
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 450);
        }
      }
    ]
  },
  {
    id: 'resources',
    label: 'Resources',
    route: '/resources',
    showFor: (_user, { isAgentLimited }) => !isAgentLimited,
    // Step index reference (for onPrevClick offsets):
    // 0  inv  click Carrier Resources tab
    // 1  vis  Browse by Type or State
    // 2  inv  ensure carrier tab has cards
    // 3  vis  Carrier Cards
    // 4  inv  click first carrier card
    // 5  vis  Carrier Details & Resources
    // 6  inv  switch to By State tab
    // 7  inv  scroll to map
    // 8  vis  By State map
    // 9  inv  click FL + scroll to carriers (combined)
    // 10 vis  Carriers in This State
    // 11 inv  click first carrier in by-state list
    // 12 vis  Carrier Details & Resources (by-state)  [showButtons: next only]
    // 13 inv  click News tab
    // 14 vis  The Current newsletter                  [onPrevClick → 12]
    // 15 inv  scroll to Industry Intel
    // 16 vis  Industry Intel
    // 17 inv  click FAQs tab
    // 18 vis  FAQ accordion list                      [onPrevClick → 16]
    // 19 inv  open accordion / click question
    // 20 vis  Answer Panel                            [onPrevClick → idx-2 = 18]
    // 21 inv  webinars scroll/setup
    // 22 vis  Webinars tab spotlight (LAST)           [onPrevClick → 20]
    steps: [
      // 0 — Invisible: click the Carrier Resources tab, ensure Health sub-tab, reset scroll
      {
        selector: () => null,
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          const tabs = document.querySelectorAll(
            '[data-tour="resources-tabs"] [role="tab"]'
          );
          Array.from(tabs)
            .find(t => t.textContent.includes('Carrier Resources'))
            ?.click();
          setTimeout(() => {
            // Ensure Health sub-tab is active
            const healthTab = Array.from(
              document.querySelectorAll(
                '[data-tour="carrier-resources-tabs"] button'
              )
            ).find(t => t.textContent.trim() === 'Health');
            healthTab?.click();
            // Reset scroll
            const container = document.querySelector(
              '[data-tour="carrier-resources-grid"]'
            );
            if (container) container.scrollTo({ top: 0 });
            driver?.moveNext();
          }, 400);
        }
      },
      // 2 — Tab switcher
      {
        selector: '[data-tour="carrier-resources-tabs"]',
        title: 'Browse by Type or State',
        description: (_user, { parentAppointmentGroups }) => {
          const groups = [...(parentAppointmentGroups || [])];
          const groupList = groups.length
            ? `${groups.map(g => `<b>${g}</b>`).join(', ')}, and more`
            : 'category';
          return `Use these tabs to filter carriers by ${groupList}. The <b>By State</b> tab shows an interactive map to browse ${companyShort} carrier partners state by state.`;
        },
        side: 'bottom',
        align: 'end',
        scrollIntoViewOptions: false,
        showButtons: ['next']
      },
      // 2b — Invisible: leave Appointed Carriers if empty; open first group tab with cards
      {
        selector: '[data-tour="carrier-resources-tabs"]',
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          ensureCarrierTabWithCards(
            '[data-tour="carrier-resources-tabs"]',
            '[data-tour="carrier-resources-grid"]',
            () => setTimeout(() => driver?.moveNext(), 300)
          );
        }
      },
      // 2 — Carrier cards grid
      {
        selector: '[data-tour="carrier-resources-grid"]',
        title: 'Carrier Cards',
        description: `Each card is an ${companyShort} carrier partner. Click any card to open the detail panel — broker contacts, enrollment portals, login credentials, and carrier-specific resource documents.`,
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 3 (invisible) — Click first carrier card to open sidebar
      {
        selector: '[data-tour="carrier-resources-grid"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          clickFirstCarrierInGrid('[data-tour="carrier-resources-grid"]');
          setTimeout(() => driver?.moveNext(), 600);
        }
      },
      // 4 — Sidebar carrier detail
      {
        selector: '[data-tour="carrier-resources-sidebar"]',
        title: 'Carrier Details & Resources',
        description:
          "This panel shows everything available for the selected carrier: broker support number, member support, enrollment portal, login credentials, and a link to the carrier's resource documents.",
        side: 'left',
        align: 'start',
        showButtons: ['next', 'previous'],
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          clickFirstCarrierInGrid('[data-tour="carrier-resources-grid"]');
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400);
        }
      },
      // 5 (invisible) — Switch to By State tab + reset scroll to prevent layout desync
      {
        selector: '[data-tour="carrier-resources-tabs"]',
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          document
            .querySelector('[data-tour="carrier-appt-by-state-tab"]')
            ?.click();
          setTimeout(() => {
            let el = document.querySelector('[data-tour="resources-tabs"]');
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                el.scrollTo({ top: 0 });
                break;
              }
              el = el.parentElement;
            }
            driver?.moveNext();
          }, 600);
        }
      },
      // 5b (invisible) — reset scroll to top so map is visible at the start of by-state tab
      {
        selector: '[data-tour="carrier-resources-tabs"]',
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const container = document.querySelector(
            '[data-tour="carrier-resources-grid"]'
          );
          if (container) container.scrollTo({ top: 0 });
          setTimeout(() => driver?.moveNext(), 450);
        }
      },
      // 6 — By State map
      {
        selector: '[data-tour="carrier-resources-grid"]',
        title: 'Carrier Partners by State',
        description: byStateMapDescription,
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          // Switch back to Health tab, click first carrier, then go to Carrier Details step
          const healthTab = Array.from(
            document.querySelectorAll(
              '[data-tour="carrier-resources-tabs"] button'
            )
          ).find(t => t.textContent.trim() === 'Health');
          healthTab?.click();
          setTimeout(() => {
            ensureCarrierTabWithCards(
              '[data-tour="carrier-resources-tabs"]',
              '[data-tour="carrier-resources-grid"]',
              () => {
                clickFirstCarrierInGrid('[data-tour="carrier-resources-grid"]');
                const idx = driver.getActiveIndex();
                setTimeout(() => driver.moveTo(idx - 3), 600);
              }
            );
          }, 400);
        }
      },
      // 9 (invisible) — Click Florida and wait for auto-scroll then advance
      {
        selector: () => null,
        title: '',
        description: '',
        side: 'right',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const stateEl =
            document.querySelector(
              '[data-tour="carrier-resources-map"] [data-name="FL"]'
            ) ||
            document.querySelector(
              '[data-tour="carrier-resources-map"] .usa-state'
            );
          if (stateEl) {
            stateEl.dispatchEvent(
              new MouseEvent('click', { bubbles: true, cancelable: true })
            );
          }
          // Component auto-scrolls after state selection — wait for it to finish
          setTimeout(() => driver?.moveNext(), 1400);
        }
      },
      // 10 — Carrier list for selected state
      {
        selector: '[data-tour="carrier-resources-by-state-carriers"]',
        title: 'Carriers in This State',
        description:
          "Every O'Neill carrier available in this state appears here, color-coded by your appointment status: <b>Confirmed</b>, <b>Pending</b>, <b>Pending Agent Action</b>, and <b>Not Appointed</b>. The small label under each carrier shows its category. Click any carrier to view its details.",
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('[data-tour="carrier-resources-state-deselect"]')
            ?.click();
          const container = document.querySelector(
            '[data-tour="carrier-resources-grid"]'
          );
          if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400);
        }
      },
      // 9 (invisible) — Click first carrier in the by-state list
      {
        selector: '[data-tour="carrier-resources-by-state-carriers"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const firstCarrier = document.querySelector(
            '[data-tour="carrier-resources-by-state-carriers"] button'
          );
          firstCarrier?.click();
          setTimeout(() => driver?.moveNext(), 600);
        }
      },
      // 13 — Sidebar from by-state selection
      {
        selector: '[data-tour="carrier-resources-sidebar"]',
        title: 'Carrier Details & Resources',
        description:
          'The same detail panel is available for every carrier you select — whether from the card grid or from the state map. Use <b>Get Appointed</b> at the bottom of the main view to request an appointment directly.',
        side: 'left',
        align: 'start',
        showButtons: ['next', 'previous'],
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          const idx = driver.getActiveIndex();
          driver.moveTo(idx - 2);
        }
      },

      // ── NEWS & INDUSTRY INTEL ─────────────────────────────────────────────
      // 14 — Invisible: click News tab + reset scroll (no spotlight — prevents iframe jump)
      {
        selector: () => null,
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          const tabs = document.querySelectorAll(
            '[data-tour="resources-tabs"] [role="tab"]'
          );
          Array.from(tabs)
            .find(t => t.textContent.includes('News'))
            ?.click();
          setTimeout(() => {
            let el = document.querySelector('[data-tour="resources-tabs"]');
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                el.scrollTo({ top: 0 });
                break;
              }
              el = el.parentElement;
            }
            driver?.moveNext();
          }, 400);
        }
      },
      // 15 — The Current newsletter section
      {
        selector: '[data-tour="news-newsletter-section"]',
        title: 'The Current: Agent Newsletter',
        description:
          "This section features the O'Neill agent newsletter — <b>The Current</b>. Each card is an issue with market news, carrier updates, and agency announcements. Click any card to read the full issue right here in the portal.",
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          // Go back to Carrier Resources tab → spotlight step 12 (by-state sidebar)
          const tabs = document.querySelectorAll(
            '[data-tour="resources-tabs"] [role="tab"]'
          );
          Array.from(tabs)
            .find(t => t.textContent.includes('Carrier Resources'))
            ?.click();
          setTimeout(() => driver.moveTo(12), 500);
        }
      },
      // 16 — Invisible: scroll down to Industry Intel
      {
        selector: '[data-tour="news-newsletter-section"]',
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="news-intel-section"] iframe'
          );
          if (target) {
            let el = target.parentElement;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay'
              ) {
                el.scrollTo({
                  top:
                    el.scrollTop +
                    target.getBoundingClientRect().top -
                    el.getBoundingClientRect().top -
                    16,
                  behavior: 'smooth'
                });
                break;
              }
              el = el.parentElement;
            }
          }
          setTimeout(() => driver?.moveNext(), 450);
        }
      },
      // 17 — Industry Intel section
      {
        selector: '[data-tour="news-intel-section"]',
        title: 'Industry Intel',
        description:
          'This section contains compliance documents, CMS resources, and other industry reference materials — updated as new regulatory guidance becomes available.',
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false
      },

      // ── FAQs ─────────────────────────────────────────────────────────────
      // 18 — Invisible: click FAQs tab + reset scroll (no spotlight — prevents iframe jump)
      {
        selector: () => null,
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          const tabs = document.querySelectorAll(
            '[data-tour="resources-tabs"] [role="tab"]'
          );
          Array.from(tabs)
            .find(t => t.textContent.trim() === 'FAQs')
            ?.click();
          setTimeout(() => {
            let el = document.querySelector('[data-tour="resources-tabs"]');
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                el.scrollTo({ top: 0 });
                break;
              }
              el = el.parentElement;
            }
            driver?.moveNext();
          }, 400);
        }
      },
      // 19 — FAQ accordion list
      {
        selector: '[data-tour="faqs-list"]',
        title: 'Frequently Asked Questions',
        description:
          'Browse questions by category. Click a category header to expand it, then click any question to see the full answer in the panel on the right.',
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          // Go back to News tab → spotlight step 16 (Industry Intel)
          const tabs = document.querySelectorAll(
            '[data-tour="resources-tabs"] [role="tab"]'
          );
          Array.from(tabs)
            .find(t => t.textContent.includes('News'))
            ?.click();
          setTimeout(() => driver.moveTo(16), 400);
        }
      },
      // 20 — Invisible: open first accordion then click first question
      {
        selector: '[data-tour="faqs-list"]',
        title: '',
        description: '',
        side: 'right',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const firstQuestion = document.querySelector(
            '[data-tour="faqs-list"] ul li button'
          );
          if (firstQuestion) {
            firstQuestion.click();
            setTimeout(() => driver?.moveNext(), 400);
          } else {
            const firstToggle = document.querySelector(
              '[data-tour="faqs-list"] div button'
            );
            firstToggle?.click();
            setTimeout(() => {
              const q = document.querySelector(
                '[data-tour="faqs-list"] ul li button'
              );
              q?.click();
              setTimeout(() => driver?.moveNext(), 400);
            }, 300);
          }
        }
      },
      // 21 — Answer panel
      {
        selector: '[data-tour="faqs-answer"]',
        title: 'Answer Panel',
        description:
          "The selected question's full answer appears here — including text, images, and video walkthroughs when available. If you still need help, use the contact button at the bottom to reach the O'Neill team.",
        side: 'left',
        align: 'start',
        showButtons: ['next', 'previous'],
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          document
            .querySelector('[data-tour="faqs-list"] ul li button')
            ?.click();
          document.querySelector('[data-tour="faqs-list"] div button')?.click();
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 2), 400); // idx - 2 = 19 (FAQ accordion)
        }
      },

      // ── WEBINARS & EVENTS ─────────────────────────────────────────────────
      // 22 (invisible) — resolver el tab de Webinars con retry loop
      {
        selector: () => null,
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        onHighlighted: (_el, _step, driver) => {
          _webinarsTabEl = null;
          let attempts = 0;
          const maxAttempts = 10;

          const tryResolve = () => {
            const container = getVisibleResourcesTabsContainer();

            if (!container && attempts < maxAttempts) {
              attempts++;
              setTimeout(tryResolve, 100);
              return;
            }

            if (!container) {
              driver?.moveNext();
              return;
            }

            let el = container;
            while (el) {
              const { overflowY } = window.getComputedStyle(el);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                el.scrollTo({ top: 0 });
                break;
              }
              el = el.parentElement;
            }

            const tabEl = resolveWebinarsTab(container);

            if (!tabEl && attempts < maxAttempts) {
              attempts++;
              setTimeout(tryResolve, 100);
              return;
            }

            _webinarsTabEl = tabEl;

            const scroller = container.querySelector('.MuiTabs-scroller');
            if (scroller && tabEl) {
              const scrollerRect = scroller.getBoundingClientRect();
              const tabRect = tabEl.getBoundingClientRect();
              const isHidden =
                tabRect.right > scrollerRect.right ||
                tabRect.left < scrollerRect.left;
              if (isHidden) {
                scroller.scrollLeft += tabRect.left - scrollerRect.left;
              }
            }

            requestAnimationFrame(() => {
              setTimeout(() => driver?.moveNext(), 350);
            });
          };

          tryResolve();
        }
      },
      // 23 — Spotlight del tab de Webinars (ÚLTIMO PASO)
      {
        selector: () => {
          if (_webinarsTabEl) {
            const rect = _webinarsTabEl.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) return _webinarsTabEl;
          }
          return resolveWebinarsTab();
        },
        title: 'Webinars & Events',
        description:
          "This tab links directly to the O'Neill event calendar on Zoom. You'll find upcoming training sessions, carrier presentations, and agency-wide webinars. Clicking the tab opens the schedule in a new window — no portal login required.",
        side: 'bottom',
        align: 'center',
        onPrevClick: (_el, _step, { driver }) => {
          const container = getVisibleResourcesTabsContainer();
          const tabs = container?.querySelectorAll('[role="tab"]') ?? [];
          Array.from(tabs)
            .find(t => t.textContent.trim() === 'FAQs')
            ?.click();
          setTimeout(() => driver.moveTo(20), 400);
        }
      }
    ]
  },
  {
    id: 'oep-updates',
    label: 'OEP Updates',
    route: '/oep-updates',
    oneillOnly: true,
    // also gated on the OEP window flag so the tour doesn't send users to the
    // closed-window placeholder, where none of the step selectors render.
    showFor: (user, { isAgentLimited }) =>
      !isAgentLimited && !!user?.isOepWindowActive,
    steps: [
      {
        selector: '[data-tour="oep-updates"]',
        title: 'OEP Updates',
        description:
          'During Open Enrollment, carriers announce which states and counties they are expanding into or exiting. This page centralizes those updates per carrier.',
        side: 'right',
        align: 'center',
        scrollIntoViewOptions: false
      },
      {
        selector: '[data-tour="oep-carriers-sidebar"]',
        title: 'Carriers Sidebar',
        description:
          'Every carrier that has published OEP updates is listed here. Use the search box at the top to filter by name, or use the arrow keys to move between carriers. Each row shows summary badges: <b>+N</b> in green for newly added states, <b>~N</b> in amber for county-level updates, and <b>−N</b> in red for full state exits. Collapse the sidebar with the panel button next to the search box to give the updates more room.',
        side: 'left',
        align: 'start',
        scrollIntoViewOptions: false
      },
      {
        selector: '[data-tour="oep-carrier-header"]',
        title: 'Carrier at a Glance',
        description:
          'The header shows the carrier description, contact information (broker/member support, emails), and quick links to their broker portal, enrollment portal, and website. Some carriers also show a colored banner above it with an important disclaimer for this OEP.',
        side: 'bottom',
        align: 'center',
        scrollIntoViewOptions: false
      },
      {
        // the notes card only renders for carriers that published one, so the
        // selector resolves lazily: driver.js centers the popover when it comes
        // back empty instead of spotlighting a stale node
        selector: () =>
          document.querySelector('[data-tour="oep-carrier-notes"]'),
        title: 'Carrier Notes',
        description:
          'When a carrier has something specific to call out this Open Enrollment — a filing quirk, a plan change, a deadline — it appears in this card right under the carrier header.',
        side: 'bottom',
        align: 'center',
        scrollIntoViewOptions: false
      },
      {
        selector: '[data-tour="oep-updates-panel"]',
        title: 'Expansions & Exits',
        description:
          'Chips are color-coded: <b>green</b> for states the carrier is newly entering, <b>amber</b> for county-level changes in states they already offer, and <b>red</b> for states they are leaving entirely. Click any state chip to see the affected counties, then scroll down for the full list of states this carrier is licensed in.',
        side: 'right',
        align: 'start',
        scrollIntoViewOptions: false
      },
      {
        selector: '[data-tour="oep-resources-panel"]',
        title: 'Carrier Resources',
        description:
          'Curated WorkDrive folder for this carrier — sales sheets, contact PDFs, and other OEP-specific materials, embedded directly here.',
        side: 'top',
        align: 'center',
        scrollIntoViewOptions: false
      }
    ]
  },
  {
    id: 'content-library',
    label: 'Content Library',
    route: '/marketing/content-library',
    oneillOnly: true,
    showFor: (_user, { isAgentLimited }) => !isAgentLimited,
    steps: [
      // 1 — Marketing Services nav link in sidebar
      {
        selector: '[data-tour="marketing-services"]',
        title: 'Marketing Services',
        description:
          'You can find the <b>Content Library</b> under <b>Marketing Services</b> in the left navigation bar.',
        side: 'right',
        align: 'center',
        scrollIntoViewOptions: false
      },
      // 2 — Category tabs
      {
        selector: '[data-tour="content-library-tabs"]',
        title: 'Content Categories',
        description:
          'Browse content by category using these tabs. <b>All</b> shows everything available — click any other tab to narrow the view to a specific content type.',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 2 — Filters + search
      {
        selector: '[data-tour="content-library-filters"]',
        title: 'Filters & Search',
        description:
          'Narrow results further by <b>Carrier</b> or <b>Material type</b>, or type in the search box to find specific assets by name or type. All filters combine together.',
        side: 'bottom',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 3 — Asset grid
      {
        selector: '[data-tour="content-library-grid"]',
        title: 'Content Assets',
        description:
          'Each card shows a marketing asset — thumbnail preview, title, and material type. Click any card to open its details in the preview panel on the right.',
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false
      },
      // 4 (invisible) — Click the first card to open the preview panel
      {
        selector: '[data-tour="content-library-grid"]',
        title: '',
        description: '',
        side: 'top',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const firstCard = document.querySelector(
            '[data-tour="content-library-grid"] button'
          );
          firstCard?.click();
          setTimeout(() => driver?.moveNext(), 500);
        }
      },
      // 5 — Preview panel (last step)
      {
        selector: '[data-tour="content-library-preview"]',
        title: 'Asset Preview',
        description:
          "This panel shows the selected asset's name, type, description, and a preview image. Use the action button at the bottom to <b>Download</b> the file or <b>Open</b> it directly — depending on the asset type.",
        side: 'left',
        align: 'start',
        scrollIntoViewOptions: false
      }
    ]
  },
  {
    id: 'leads',
    label: 'Leads',
    route: '/marketing/leads',
    oneillOnly: true,
    showFor: (user, { isAgentLimited }) =>
      !isAgentLimited && isLeadPartnersTabVisible(user),
    steps: [
      // 1 — Partner cards overview (scroll to top)
      {
        selector: '[data-tour="leads-partners-cards"]',
        title: 'Lead Partners',
        description:
          "O'Neill has exclusive partnerships with lead providers that give you access to high-intent prospects. Click on any partner card to see details and apply.",
        side: 'bottom',
        align: 'center',
        scrollIntoViewOptions: false,
        onHighlighted: el => {
          let container = el?.parentElement;
          while (container) {
            const { overflowY } = window.getComputedStyle(container);
            if (overflowY === 'auto' || overflowY === 'scroll') {
              container.scrollTo({ top: 0, behavior: 'smooth' });
              break;
            }
            container = container.parentElement;
          }
        }
      },
      // 2 (invisible) — Click the RateQuote card to reveal the detail panel
      {
        selector: '[data-tour="leads-partners-cards"]',
        title: '',
        description: '',
        side: 'bottom',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const rqCard = document.querySelector(
            '[data-tour="leads-partners-cards"] button:not([disabled])'
          );
          rqCard?.click();
          setTimeout(() => driver?.moveNext(), 500);
        }
      },
      // 3a (invisible) — scroll to RateQuote panel before spotlighting it
      {
        selector: '[data-tour="leads-partners-cards"]',
        title: '',
        description: '',
        hideButtons: true,
        scrollIntoViewOptions: false,
        onHighlighted: (_el, _step, driver) => {
          const target = document.querySelector(
            '[data-tour="leads-rate-quote-panel"]'
          );
          if (target) {
            let container = target.parentElement;
            while (container) {
              const { overflowY } = window.getComputedStyle(container);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                const containerRect = container.getBoundingClientRect();
                const targetRect = target.getBoundingClientRect();
                container.scrollTo({
                  top: Math.max(
                    0,
                    container.scrollTop +
                      (targetRect.top - containerRect.top) -
                      40
                  ),
                  behavior: 'smooth'
                });
                break;
              }
              container = container.parentElement;
            }
          }
          setTimeout(() => driver.moveNext(), 450);
        }
      },
      // 3b — RateQuote detail panel
      {
        selector: '[data-tour="leads-rate-quote-panel"]',
        title: 'RateQuote Partnership',
        description:
          'RateQuote offers discounted inbound ACA leads through live call transfers driven by real-time paid search. These are high-intent consumers actively looking for coverage. Review the eligibility criteria and details before applying.',
        side: 'top',
        align: 'start',
        scrollIntoViewOptions: false,
        onPrevClick: (_el, _step, { driver }) => {
          // Deselect by clicking the active card again to close the panel
          const rqCard = document.querySelector(
            '[data-tour="leads-partners-cards"] button:not([disabled])'
          );
          rqCard?.click();
          // Scroll back to top
          const target = document.querySelector(
            '[data-tour="leads-partners-cards"]'
          );
          if (target) {
            let container = target.parentElement;
            while (container) {
              const { overflowY } = window.getComputedStyle(container);
              if (overflowY === 'auto' || overflowY === 'scroll') {
                container.scrollTo({ top: 0, behavior: 'smooth' });
                break;
              }
              container = container.parentElement;
            }
          }
          // 3 steps back (2 invisible scroll/click + partner cards)
          const idx = driver.getActiveIndex();
          setTimeout(() => driver.moveTo(idx - 3), 500);
        }
      },
      // 4 — Apply Now button (last step)
      {
        selector: '[data-tour="leads-apply-now"]',
        title: 'Apply Now',
        description:
          "When you're ready, click <b>Apply Now</b> to fill out a short application form. Once submitted, a notification is sent to the O'Neill team letting them know you're interested — they'll review your agency's fit and get back to you within 2–3 business days.",
        side: 'top',
        align: 'center',
        scrollIntoViewOptions: false,
        onHighlighted: el => {
          let container = el?.parentElement;
          while (container) {
            const { overflowY } = window.getComputedStyle(container);
            if (overflowY === 'auto' || overflowY === 'scroll') {
              const containerRect = container.getBoundingClientRect();
              const elRect = el.getBoundingClientRect();
              const targetTop =
                container.scrollTop + (elRect.top - containerRect.top) - 40;
              container.scrollTo({
                top: Math.max(0, targetTop),
                behavior: 'smooth'
              });
              break;
            }
            container = container.parentElement;
          }
        }
      }
    ]
  }
];
